// useFieldModalState.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEnumCompatibleFieldType, isOptionsListFieldType } from 'utilities/FieldTypes';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useChecklistItemEditor from 'admin/nodetypes/fields/hooks/useChecklistItemEditor';
import useEnumOptionEditor from 'admin/nodetypes/fields/hooks/useEnumOptionEditor';

// Utilities
import { getChecklistTemplateItemLabel } from 'utilities/ChecklistTemplateItems';
import { isTemporalFieldType } from 'views/utils/temporalFieldFormat';
import {
    DEFAULT_VALUE_UNSUPPORTED_TYPES,
    FIELD_TYPE_DESCRIPTIONS,
    FIELD_TYPE_ICON_KEYS,
    FIELD_TYPES,
    FIELD_TYPE_LABELS,
    makeDraft,
} from '../constants';
import { buildDraftFromField, buildNextFieldFromDraft, patchDraftWithRules } from '../fieldDraft';
import { resolveUnavailableOnceOnlyTypes } from '../fieldTypeAvailability';
import { validateDraft } from '../validation';

const EMPTY_ERRORS = {
    type: '',
    label: '',
    options: '',
    snapTo: '',
    targetType: '',
    defaultUnit: '',
    currencyCode: '',
};

const sortTypeOptionsByLabel = (options) => (
    [...options].sort((left, right) => (
        String(left?.label || '').localeCompare(String(right?.label || ''), undefined, {
            sensitivity: 'base',
        })
    ))
);

const areEnumOptionListsEqual = (leftOptions, rightOptions) => {
    const left = Array.isArray(leftOptions) ? leftOptions : [];
    const right = Array.isArray(rightOptions) ? rightOptions : [];
    if (left.length !== right.length) return false;

    for (let index = 0; index < left.length; index += 1) {
        const leftOption = left[index] || {};
        const rightOption = right[index] || {};

        if (String(leftOption.id || '') !== String(rightOption.id || '')) return false;
        if (String(leftOption.value || '') !== String(rightOption.value || '')) return false;
        if (String(leftOption.color || '') !== String(rightOption.color || '')) return false;
        if (String(leftOption.iconKey || '') !== String(rightOption.iconKey || '')) return false;

        const leftEnLabel = String(leftOption?.translations?.en?.label || '').trim();
        const rightEnLabel = String(rightOption?.translations?.en?.label || '').trim();
        if (leftEnLabel !== rightEnLabel) return false;
    }

    return true;
};

const areDraftsEqual = (leftDraft, rightDraft) => {
    const left = leftDraft && typeof leftDraft === 'object' ? leftDraft : {};
    const right = rightDraft && typeof rightDraft === 'object' ? rightDraft : {};

    if (String(left.label || '') !== String(right.label || '')) return false;
    if (String(left.fieldDescription || '') !== String(right.fieldDescription || '')) return false;
    if (String(left.type || '') !== String(right.type || '')) return false;
    if (String(left.targetType || '') !== String(right.targetType || '')) return false;
    if (Boolean(left.allowMultiple !== false) !== Boolean(right.allowMultiple !== false)) return false;
    if (Boolean(left.showFilter === true) !== Boolean(right.showFilter === true)) return false;
    if (Boolean(left.showAccess === true) !== Boolean(right.showAccess === true)) return false;
    if (Boolean(left.showAsProfileStack === true) !== Boolean(right.showAsProfileStack === true)) return false;
    if (Boolean(left.includeInQrPrint === true) !== Boolean(right.includeInQrPrint === true)) return false;
    if (Boolean(left.displayInDefaultLists === true) !== Boolean(right.displayInDefaultLists === true)) return false;
    if (String(left.default ?? '') !== String(right.default ?? '')) return false;
    if (String(left.defaultUnit || '') !== String(right.defaultUnit || '')) return false;
    if (String(left.enumOptionsSort || '') !== String(right.enumOptionsSort || '')) return false;
    if (String(left.snapTo ?? '') !== String(right.snapTo ?? '')) return false;
    if (String(left.currencyCode || '') !== String(right.currencyCode || '')) return false;
    if (String(left.calendarColor || '') !== String(right.calendarColor || '')) return false;
    if (Boolean(left.allowCustomItems === true) !== Boolean(right.allowCustomItems === true)) return false;

    const leftDisplayFields = Array.isArray(left.displayFields) ? left.displayFields : [];
    const rightDisplayFields = Array.isArray(right.displayFields) ? right.displayFields : [];
    if (leftDisplayFields.length !== rightDisplayFields.length) return false;
    for (let index = 0; index < leftDisplayFields.length; index += 1) {
        if (String(leftDisplayFields[index] || '') !== String(rightDisplayFields[index] || '')) {
            return false;
        }
    }

    const leftChecklistItems = Array.isArray(left.checklistItems) ? left.checklistItems : [];
    const rightChecklistItems = Array.isArray(right.checklistItems) ? right.checklistItems : [];
    if (leftChecklistItems.length !== rightChecklistItems.length) return false;
    for (let index = 0; index < leftChecklistItems.length; index += 1) {
        const leftItem = leftChecklistItems[index] || {};
        const rightItem = rightChecklistItems[index] || {};
        if (String(leftItem.id || '') !== String(rightItem.id || '')) return false;
        if (String(leftItem.key || '') !== String(rightItem.key || '')) return false;
        if (getChecklistTemplateItemLabel(leftItem) !== getChecklistTemplateItemLabel(rightItem)) {
            return false;
        }
    }

    return areEnumOptionListsEqual(left.options, right.options);
};

/**
 * useFieldModalState
 *
 * Manages draft state, derived options, enum-option editing, and save validation for FieldModal.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isOpen - Whether the modal is open.
 * @param {function} params.onSave - Callback when the field is saved.
 * @param {object|null} [params.editingField=null] - Existing field being edited.
 * @param {string} [params.nodeTypeLabel=''] - Display label for the current node type.
 * @param {array} [params.existingFields=[]] - Existing schema fields for once-only checks.
 * @param {string} [params.typeKey=''] - Current node type key for field ownership checks.
 *
 * @returns {object} Grouped state and handlers for the FieldModal shell.
 */
export default function useFieldModalState({
    isOpen = false,
    draft: controlledDraft,
    onDraftChange,
    onSave,
    editingField = null,
    nodeTypeLabel = '',
    existingFields = [],
    allowedFieldTypes = null,
    typeKey = '',
}) {
    const { getAll, getLabelPlural } = useNodeTypes();

    const isControlled = controlledDraft !== undefined && typeof onDraftChange === 'function';
    const [internalDraft, setInternalDraft] = useState(makeDraft);
    const draft = isControlled ? controlledDraft : internalDraft;

    const setDraft = useCallback((updater) => {
        if (isControlled) {
            const nextDraft = typeof updater === 'function'
                ? updater(controlledDraft)
                : updater;
            onDraftChange(nextDraft);
            return;
        }

        setInternalDraft(updater);
    }, [controlledDraft, isControlled, onDraftChange]);
    const [errors, setErrors] = useState(EMPTY_ERRORS);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    const isEditing = Boolean(editingField);

    const resolvedAllowedFieldTypes = useMemo(() => {
        if (!Array.isArray(allowedFieldTypes) || allowedFieldTypes.length === 0) {
            return FIELD_TYPES;
        }

        const allowedSet = new Set(
            allowedFieldTypes
                .map((value) => String(value || '').trim())
                .filter(Boolean)
        );

        return FIELD_TYPES.filter((value) => allowedSet.has(value));
    }, [allowedFieldTypes]);

    const unavailableOnceOnlyTypes = useMemo(() => (
        resolveUnavailableOnceOnlyTypes({
            existingFields,
            editingFieldName: editingField?.name,
            typeKey,
        })
    ), [editingField?.name, existingFields, typeKey]);

    const typeOptions = useMemo(() => (
        sortTypeOptionsByLabel(
            resolvedAllowedFieldTypes
                .filter((value) => isEditing || !unavailableOnceOnlyTypes.includes(value))
                .map((value) => ({
                    value,
                    label: FIELD_TYPE_LABELS[value] || value,
                    iconKey: FIELD_TYPE_ICON_KEYS[value] || 'category',
                    note: FIELD_TYPE_DESCRIPTIONS[value] || '',
                }))
        )
    ), [isEditing, resolvedAllowedFieldTypes, unavailableOnceOnlyTypes]);

    const mergedTypeOptions = useMemo(() => {
        if (!isEditing || !draft.type) return typeOptions;
        if (resolvedAllowedFieldTypes.includes(draft.type)) return typeOptions;

        return sortTypeOptionsByLabel([
            ...typeOptions,
            {
                value: draft.type,
                label: FIELD_TYPE_LABELS[draft.type] || draft.type,
                iconKey: FIELD_TYPE_ICON_KEYS[draft.type] || 'category',
                note: FIELD_TYPE_DESCRIPTIONS[draft.type] || '',
            },
        ]);
    }, [draft.type, isEditing, resolvedAllowedFieldTypes, typeOptions]);

    const targetTypeOptions = useMemo(() => {
        const typeRows = Array.isArray(getAll?.()) ? getAll() : [];

        return typeRows
            .map((type) => {
                const key = String(type?.key || '').trim();
                // USER is modeled via people fields, not nodelist relations.
                // System types must remain selectable (product schemas are system).
                if (!key || key.toUpperCase() === 'USER') return null;

                return {
                    value: key,
                    label: getLabelPlural(key, 'en', { fallback: key }),
                };
            })
            .filter(Boolean)
            .sort((left, right) => String(left.label || '').localeCompare(String(right.label || '')));
    }, [getAll, getLabelPlural]);

    /**
     * patchDraft
     *
     * Applies a partial draft update while preserving field-type-specific draft rules.
     *
     * @param {object} patch - Partial field draft values to merge into the current draft.
     */
    const patchDraft = useCallback((patch) => {
        if (!patch || typeof patch !== 'object') return;
        setDraft((prev) => patchDraftWithRules({ prevDraft: prev, patch, isEditing }));
    }, [isEditing, setDraft]);

    const {
        selectedEnumOptions,
        optionValueDrafts,
        setOptionValueDrafts,
        handleOptionAdd,
        handleOptionRemove,
        handleOptionValueCommit,
        handleOptionValueKeyDown,
        handleOptionColorChange,
        handleOptionIconChange,
        handleOptionMove,
    } = useEnumOptionEditor({
        selected: draft,
        selectedIndex: 0,
        patchSelected: patchDraft,
    });

    const {
        checklistItems,
        itemLabelDrafts,
        setItemLabelDrafts,
        handleChecklistItemAdd,
        handleChecklistItemRemove,
        handleChecklistItemLabelCommit,
        handleChecklistItemMoveByDelta,
    } = useChecklistItemEditor({
        selected: draft,
        selectedIndex: 0,
        patchSelected: patchDraft,
    });

    const isEnumType = isEnumCompatibleFieldType(draft.type);
    const isMeasurementType = draft.type === 'measurement';
    const isOptionsType = isOptionsListFieldType(draft.type) || isMeasurementType;
    const isChecklistType = draft.type === 'checklist';
    const isNodeListType = draft.type === 'relation' || draft.type === 'nodelist';
    const isPeopleType = draft.type === 'people';
    const isTagsType = draft.type === 'tags';
    const isTemporalType = isTemporalFieldType(draft.type);
    const isDefaultUnsupported = DEFAULT_VALUE_UNSUPPORTED_TYPES.includes(draft.type);
    const resolvedNodeTypeLabel = typeof nodeTypeLabel === 'string' ? nodeTypeLabel.trim() : '';

    const modalTitle = useMemo(() => {
        if (!resolvedNodeTypeLabel) {
            return isEditing ? 'Edit Field' : 'Add Field';
        }

        return isEditing
            ? `Edit "${resolvedNodeTypeLabel}" Field`
            : `Add Field to "${resolvedNodeTypeLabel}"`;
    }, [isEditing, resolvedNodeTypeLabel]);

    useEffect(() => {
        if (isControlled) return;
        if (!isOpen) return;

        const nextDraft = isEditing
            ? buildDraftFromField(editingField)
            : makeDraft();

        setInternalDraft((prev) => (areDraftsEqual(prev, nextDraft) ? prev : nextDraft));

        setErrors((prev) => {
            const previous = prev && typeof prev === 'object' ? prev : EMPTY_ERRORS;
            if (
                String(previous.type || '') === ''
                && String(previous.label || '') === ''
                && String(previous.options || '') === ''
                && String(previous.snapTo || '') === ''
                && String(previous.targetType || '') === ''
                && String(previous.defaultUnit || '') === ''
                && String(previous.currencyCode || '') === ''
            ) {
                return prev;
            }

            return EMPTY_ERRORS;
        });
    }, [editingField, isControlled, isEditing, isOpen]);

    /**
     * clearError
     *
     * Clears one validation error entry after the user edits that field.
     *
     * @param {string} errorKey - Error key to reset.
     */
    const clearError = useCallback((errorKey) => {
        setErrors((prev) => ({
            ...prev,
            [errorKey]: '',
        }));
    }, []);

    /**
     * handleSave
     *
     * Validates the current draft and emits the normalized field definition when valid.
     */
    const handleSave = useCallback(() => {
        const validation = validateDraft(draft, {
            unavailableTypes: unavailableOnceOnlyTypes,
        });

        if (!validation.isValid) {
            setErrors(validation.errors);
            return;
        }

        const nextField = buildNextFieldFromDraft(
            isEnumCompatibleFieldType(draft.type)
                ? { ...draft, enumOptionsSort: 'manual' }
                : draft
        );

        if (typeof onSave === 'function') {
            onSave(nextField);
        }
    }, [draft, onSave, unavailableOnceOnlyTypes]);

    const canDelete = isEditing;
    const deleteLabel = editingField?.label || 'this field';

    return {
        modalState: {
            modalTitle,
            isEditing,
            canDelete,
            deleteLabel,
            confirmDeleteOpen,
            setConfirmDeleteOpen,
        },
        formState: {
            draft,
            errors,
            mergedTypeOptions,
            targetTypeOptions,
            isEnumType,
            isMeasurementType,
            isOptionsType,
            isChecklistType,
            isNodeListType,
            isPeopleType,
            isTagsType,
            isTemporalType,
            isDefaultUnsupported,
            patchDraft,
            clearError,
            handleSave,
        },
        checklistItemsState: {
            checklistItems,
            itemLabelDrafts,
            setItemLabelDrafts,
            handleChecklistItemAdd,
            handleChecklistItemRemove,
            handleChecklistItemLabelCommit,
            handleChecklistItemMoveByDelta,
        },
        enumOptionsState: {
            selectedEnumOptions,
            optionValueDrafts,
            setOptionValueDrafts,
            handleOptionAdd,
            handleOptionRemove,
            handleOptionValueCommit,
            handleOptionValueKeyDown,
            handleOptionColorChange,
            handleOptionIconChange,
            handleOptionMove,
        },
    };
}